/**
 * Shared Upload Types
 *
 * Common types used across file upload components for consistent
 * state management and error handling.
 *
 * @module lib/uploadTypes
 */

// ============================================================================
// PROGRESS PHASE CONSTANTS
// ============================================================================

/**
 * Progress phase mapping constants
 *
 * Upload and processing phases are mapped to a single 0-100 range to prevent backward jumps:
 * - Upload phase (0-100%) displays as (0-80%) — network transfer is the slow part
 * - Processing phase (0-100%) displays as (80-100%) — server processing is fast
 */
export const UPLOAD_PHASE_WEIGHT = 0.8;
export const PROCESSING_PHASE_WEIGHT = 0.2;
export const UPLOAD_PHASE_END_PERCENT = 80;

/**
 * Processing step thresholds for step-indicator UIs
 */
export const PROCESSING_STEPS = [
  { id: 'upload', label: 'Uploading file', threshold: UPLOAD_PHASE_END_PERCENT },
  { id: 'process', label: 'Processing data', threshold: 99 },
  { id: 'complete', label: 'Complete', threshold: 100 },
] as const;

// ============================================================================
// UPLOAD STATE TYPES
// ============================================================================

/**
 * Visual state of an upload operation
 *
 * - `idle`: No upload in progress, ready to accept files
 * - `uploading`: File is being uploaded to server
 * - `processing`: Server is processing the uploaded file
 * - `completed`: Upload and processing completed successfully
 * - `failed`: Upload or processing failed with error
 */
export type UploadVisualState = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * Standard error codes for upload operations
 */
export type UploadErrorCode =
  | 'VALIDATION_FAILED'
  | 'UPLOAD_FAILED'
  | 'PROCESSING_FAILED'
  | 'UNSUPPORTED_FILE_TYPE'
  | 'FILE_TOO_LARGE'
  | 'NETWORK_ERROR'
  | 'QUOTA_EXCEEDED'
  | 'UNKNOWN_ERROR';

/**
 * Structured error information for upload failures
 */
export interface UploadError {
  /** Error code for programmatic handling */
  code: UploadErrorCode;
  /** Human-readable error message */
  message: string;
  /** Original error object (optional) */
  originalError?: Error;
}

// ============================================================================
// FILE VALIDATION TYPES
// ============================================================================

/**
 * Result of file validation
 */
export interface FileValidationResult {
  /** Whether the file is valid */
  valid: boolean;
  /** Error information if invalid */
  error?: UploadError;
}

/**
 * Configuration for file validation
 */
export interface FileValidationConfig {
  /** Maximum file size in bytes */
  maxSizeBytes?: number;
  /** Allowed MIME types */
  allowedTypes?: string[];
  /** Allowed file extensions (with or without dot) */
  allowedExtensions?: string[];
}
